// "Use this from another AI client" instructions, generated from a provider's
// connection metadata (server URL, which client uses which auth mode, the
// API-key header and prefix). Pure: no React, no network. It never receives a
// credential -- every snippet carries the provider's placeholder -- so it
// cannot print a real key. Adapted from platform-frontend PR #2208's
// mcp-access generator.
import type {
  ConnectionAuthMode,
  ExternalClientId,
  PersonalConnection,
} from '@/services/personal-connections/types';

export interface ClientSnippet {
  id: string;
  label: string;
  text: string;
}

export interface ClientSetup {
  client: ExternalClientId;
  label: string;
  method: ConnectionAuthMode;
  methodLabel: string;
  steps: string[];
  snippets: ClientSnippet[];
  testPrompt: string;
}

export const CLIENT_LABELS: Record<ExternalClientId, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  cursor: 'Cursor',
  other: 'Other clients',
};

const METHOD_LABELS: Record<ConnectionAuthMode, string> = {
  oauth: 'Sign in with your account (OAuth)',
  api_key: 'Personal API key in a header',
};

type Provider = Pick<
  PersonalConnection,
  | 'provider'
  | 'name'
  | 'server_url'
  | 'external_clients'
  | 'api_key'
  | 'example_prompts'
>;

export const headerValue = (provider: Provider): string =>
  provider.api_key
    ? `${provider.api_key.scheme} ${provider.api_key.placeholder}`.trim()
    : '';

const slug = (provider: Provider) =>
  provider.provider.toLowerCase().replace(/[^a-z0-9-]+/g, '-');

const keyStep = (provider: Provider) => {
  const spec = provider.api_key;
  if (!spec) return '';
  return `${spec.where_to_create} It starts with ${spec.prefix}. Keys are personal: never share one or paste it into a chat.`;
};

export const clientSetups = (provider: Provider): ClientSetup[] => {
  const url = provider.server_url;
  if (!url) return [];
  const testPrompt =
    provider.example_prompts[0] ?? `What can ${provider.name} tell me?`;
  return provider.external_clients
    .filter(({ auth }) => auth === 'oauth' || Boolean(provider.api_key))
    .map(({ client, auth }) => {
      const label = CLIENT_LABELS[client];
      const base = {
        client,
        label,
        method: auth,
        methodLabel: METHOD_LABELS[auth],
        testPrompt,
      };
      const urlSnippet = { id: 'server_url', label: 'Server URL', text: url };
      if (auth === 'oauth') {
        return {
          ...base,
          steps: [
            `In ${label}, add a custom connector (remote MCP server) with the server URL below.`,
            `When ${label} asks, sign in to ${provider.name} as yourself and approve access.`,
          ],
          snippets: [urlSnippet],
        };
      }
      const header = provider.api_key?.header ?? 'Authorization';
      if (client === 'cursor') {
        return {
          ...base,
          steps: [
            keyStep(provider),
            'Open ~/.cursor/mcp.json and add the config below. Never commit a file that holds a key.',
            `Replace ${provider.api_key?.placeholder} with your key on your own machine, then restart Cursor.`,
          ],
          snippets: [
            {
              id: 'cursor_config',
              label: 'Cursor config (mcp.json)',
              text: JSON.stringify(
                {
                  mcpServers: {
                    [slug(provider)]: {
                      url,
                      headers: { [header]: headerValue(provider) },
                    },
                  },
                },
                null,
                2,
              ),
            },
          ],
        };
      }
      return {
        ...base,
        steps: [
          keyStep(provider),
          `Give the client the server URL and send your key in the ${header} header (Streamable HTTP transport).`,
        ],
        snippets: [
          urlSnippet,
          {
            id: 'auth_header',
            label: `${header} header`,
            text: `${header}: ${headerValue(provider)}`,
          },
        ],
      };
    });
};
